// Funding › Central Payments MFA: QOR's money at a processor, checked on its own (client's ask 2026-10-31).
// - what QOR holds for partners (their funds) against what the MFA itself shows, and QOR's ledger there;
// - the MFA's details and every reading of its balance (hourly from CP, or typed in);
// - the money QOR wired there: asked (waiting for a second system administrator), approved or turned down, and the
//   ledger movements it booked. Another processor (InterCash…) is chosen above the figures and reads the same way.
import { shortDate } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { fundsApi, type Approval, type FundsHealth } from '../api';
import { ApprovalsTable, approvalKind } from '../components/Approvals';
import type { FundingTabProps } from './Funding';
import { Pager } from './FundingPartnersTab';
import { amountOf, cents, entryLabel, providerLabel } from './fundingLabels';

const WIRE_KINDS = ['processor_topup', 'processor_adjustment'];

export function FundingProcessorTab({
  editable,
  busy,
  setBusy,
  done,
  decide,
  health: h,
  pending,
}: FundingTabProps & { health: FundsHealth | null; pending: Approval[] | null }) {
  const markets = h?.processors ?? [];
  const [chosen, setChosen] = useState<string | null>(null);
  const current = chosen ?? (markets.some((m) => m.provider === 'opencp') ? 'opencp:USD' : null) ?? 'opencp:USD';
  const [provider, currency] = current.split(':') as [string, string];
  const p = markets.find((m) => m.provider === provider && m.currency === currency) ?? null;
  const name = providerLabel(provider);
  const isCp = provider === 'opencp';

  const live = useQuery({
    queryKey: ['opencp-account'],
    queryFn: () => fundsApi.openCpAccount(),
    enabled: isCp && editable,
  });
  const account = live.data?.ok && live.data.data.configured ? live.data.data : null;

  const [requestsPage, setRequestsPage] = useState(1);
  const requests = useQuery({
    queryKey: ['approvals', 'all', 'processor', requestsPage],
    queryFn: () => fundsApi.approvals(null, requestsPage, 'processor'),
  });
  const [everyMovement, setEveryMovement] = useState(false);
  const [entriesPage, setEntriesPage] = useState(1);
  const entries = useQuery({
    queryKey: ['processor-entries', provider, currency, everyMovement, entriesPage],
    queryFn: () => fundsApi.processorEntries(provider, currency, entriesPage, everyMovement ? undefined : WIRE_KINDS),
  });
  const [readsPage, setReadsPage] = useState(1);
  const reads = useQuery({
    queryKey: ['processor-snapshots', provider, currency, readsPage],
    queryFn: () => fundsApi.processorSnapshots(provider, currency, readsPage),
  });

  const [balance, setBalance] = useState('');
  const [wire, setWire] = useState({ kind: 'topup' as 'topup' | 'adjustment', amount: '', reference: '', memo: '' });

  // the MFA's own balance: read live when allowed, otherwise its last reading
  const mfa = account?.available ?? p?.snapshot?.balance ?? null;
  const mfaWhen = account ? 'read live just now' : p?.snapshot ? `last read ${shortDate(p.snapshot.takenAt)}` : null;
  const partners = p?.partnerTotal ?? '0.00';
  const cover = mfa === null ? null : cents(mfa) - cents(partners);
  const ledgerDifference = mfa === null || p?.ledger == null ? null : cents(mfa) - cents(p.ledger);

  async function readNow() {
    setBusy(true);
    if (isCp) {
      const r = await fundsApi.readCentralPayments();
      await done(
        r.ok && r.data.length > 0,
        !r.ok
          ? r.error
          : r.data.length > 0
            ? 'Read the Central Payments funding account (MFA).'
            : 'The Central Payments funding account could not be read.',
      );
    } else {
      const r = await fundsApi.readInterCash();
      await done(r.ok, r.ok ? `Read ${r.data.length} InterCash balance(s).` : r.error);
    }
  }
  async function recordBalance(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await fundsApi.recordBalance({ provider, currency, balance: balance.trim() });
    if (r.ok) setBalance('');
    await done(r.ok, r.ok ? `${name}'s balance recorded and compared with QOR’s ledger.` : r.error);
  }
  async function askWire(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await fundsApi.requestProcessor({
      kind: wire.kind,
      provider,
      currency,
      amount: wire.amount.trim(),
      ...(wire.reference.trim() && { reference: wire.reference.trim() }),
      ...(wire.memo.trim() && { memo: wire.memo.trim() }),
    });
    if (r.ok) setWire({ ...wire, amount: '', reference: '', memo: '' });
    await done(
      r.ok,
      r.ok ? `${approvalKind(r.data.kind)} asked: a second system administrator must approve it.` : r.error,
    );
  }

  const options = [
    ...new Set([
      ...(markets.some((m) => m.provider === 'opencp') ? [] : ['opencp:USD']),
      ...markets.map((m) => `${m.provider}:${m.currency}`),
    ]),
  ];

  return (
    <>
      {options.length > 1 && (
        <div className="qc-cardholder-filters" style={{ marginBottom: 12 }}>
          <label>
            <span>Processor</span>
            <select value={current} onChange={(e) => setChosen(e.target.value)}>
              {options.map((o) => {
                const [prov, cur] = o.split(':');
                return (
                  <option key={o} value={o}>
                    {providerLabel(prov ?? null)} {cur}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
      )}

      <section className="qc-stat-row" aria-label={`QOR's money at ${name}`}>
        <div className="qc-stat-tile">
          <span className="qc-stat-label">QOR holds for partners</span>
          <strong className="qc-stat-value">{partners}</strong>
          <span className="qc-stat-trend">
            {currency} · partner funds{p ? ` (${p.partnerAvailable} available + ${p.partnerHeld} held)` : ''}
          </span>
        </div>
        <div className="qc-stat-tile qc-stat-tile--success">
          <span className="qc-stat-label">{isCp ? 'MFA balance' : `${name} balance`}</span>
          <strong className="qc-stat-value">{mfa ?? '—'}</strong>
          <span className="qc-stat-trend">{mfaWhen ?? 'Never read'}</span>
        </div>
        <div className={`qc-stat-tile ${cover !== null && cover < 0 ? 'qc-stat-tile--red' : 'qc-stat-tile--purple'}`}>
          <span className="qc-stat-label">
            {cover !== null && cover < 0 ? 'Short of partners’ money' : 'Above partners’ money'}
          </span>
          <strong className="qc-stat-value">{cover === null ? '—' : amountOf(Math.abs(cover))}</strong>
          <span className="qc-stat-trend">
            {isCp ? 'MFA' : 'Balance'} − partner funds{p?.coverage ? ` · coverage ${p.coverage}` : ''}
          </span>
        </div>
        <div className="qc-stat-tile">
          <span className="qc-stat-label">QOR ledger at {name}</span>
          <strong className="qc-stat-value">{p?.ledger ?? '—'}</strong>
          <span className="qc-stat-trend">
            {ledgerDifference === null
              ? 'Wired in, less loads out'
              : ledgerDifference === 0
                ? 'Matches the balance'
                : `${isCp ? 'MFA' : 'Balance'} differs by ${amountOf(ledgerDifference)}`}{' '}
            {p?.mismatch && <span className="qc-badge qc-badge-danger">Mismatch</span>}
          </span>
        </div>
      </section>

      <div className="qc-detail-row">
        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3>{isCp ? 'The MFA' : name}</h3>
            <span>
              {isCp
                ? 'Central Payments keeps QOR’s money on a funding card (MFA): every card load draws on it at once.'
                : `QOR’s account at ${name}.`}
            </span>
          </div>
          <dl className="qc-detail-dl">
            {isCp && (
              <div>
                <dt>Funding card</dt>
                <dd>
                  {account
                    ? account.cardId
                    : live.data && !live.data.ok
                      ? `Not read: ${live.data.error}`
                      : live.data?.ok && !live.data.data.configured
                        ? 'Not set up (OPENCP_FUNDING_CARD_ID)'
                        : editable
                          ? '…'
                          : 'Shown to system administrators'}
                </dd>
              </div>
            )}
            <div>
              <dt>Last reading</dt>
              <dd>
                {p?.snapshot ? (
                  <>
                    {p.snapshot.balance}{' '}
                    <small>({p.snapshot.source === 'api' ? 'read from the processor' : 'typed in'})</small>,{' '}
                    {shortDate(p.snapshot.takenAt)}{' '}
                    {p.snapshotStale && <span className="qc-badge qc-badge-warning">Old</span>}
                  </>
                ) : (
                  <span className="qc-badge qc-badge-warning">Never read</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Loads in flight</dt>
              <dd>
                {p?.inFlight ?? '0.00'} <small>(sent, not settled: the balance may already show them)</small>
              </dd>
            </div>
            <div>
              <dt>Advances out</dt>
              <dd>
                {p?.advancesOutstanding ?? '0.00'} <small>(lent to partners by QOR)</small>
              </dd>
            </div>
          </dl>
          {editable && (
            <form onSubmit={(e) => void recordBalance(e)} className="qc-account-field" style={{ marginTop: 14 }}>
              <label>
                <span>Balance from the {isCp ? 'Central Payments portal' : 'processor’s portal'} or a statement</span>
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  placeholder="0.00"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                />
              </label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                  Record balance
                </button>
                {(isCp || provider === 'intercash') && (
                  <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void readNow()}>
                    Read {isCp ? 'the MFA' : name} now
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {editable && (
          <div className="qc-panel">
            <div className="qc-panel-title">
              <h3>Money QOR wired {isCp ? 'to the MFA' : `to ${name}`}</h3>
              <span>
                Or a correction of QOR&apos;s ledger there (the first one opens the ledger). Needs a second system
                administrator.
              </span>
            </div>
            <form onSubmit={(e) => void askWire(e)} className="qc-account-field">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                <label>
                  <span>What</span>
                  <select
                    value={wire.kind}
                    onChange={(e) => setWire({ ...wire, kind: e.target.value as 'topup' | 'adjustment' })}
                  >
                    <option value="topup">Wire sent to the processor</option>
                    <option value="adjustment">Ledger correction (+ or −)</option>
                  </select>
                </label>
                <label>
                  <span>Amount ({currency})</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    placeholder={wire.kind === 'adjustment' ? '-25.00 or 25.00' : '0.00'}
                    value={wire.amount}
                    onChange={(e) => setWire({ ...wire, amount: e.target.value })}
                  />
                </label>
              </div>
              <label>
                <span>Reference (the bank&apos;s wire number)</span>
                <input
                  type="text"
                  value={wire.reference}
                  onChange={(e) => setWire({ ...wire, reference: e.target.value })}
                />
              </label>
              <label>
                <span>Note{wire.kind === 'adjustment' ? ' (required: why)' : ''}</span>
                <input
                  type="text"
                  required={wire.kind === 'adjustment'}
                  value={wire.memo}
                  onChange={(e) => setWire({ ...wire, memo: e.target.value })}
                />
              </label>
              <div>
                <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                  Ask for approval
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Waiting for approval</h3>
          <span>Wires and ledger corrections asked, before a second system administrator approves them.</span>
        </div>
        {pending && <ApprovalsTable items={pending} busy={busy} onDecide={(a, ok) => void decide(a, ok)} />}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Transfer orders</h3>
          <span>Every wire and correction asked, of every processor: who asked, who decided, and how it ended.</span>
        </div>
        {requests.data && (
          <>
            <ApprovalsTable
              items={requests.data.items}
              busy={busy}
              onDecide={(a, ok) => void decide(a, ok)}
              empty="No wire or correction asked yet."
            />
            {requests.data.total > requests.data.pageSize && (
              <Pager
                page={requestsPage}
                pageSize={requests.data.pageSize}
                total={requests.data.total}
                onPage={setRequestsPage}
              />
            )}
          </>
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>QOR ledger at {name}</h3>
          <span>
            {everyMovement ? 'Every movement, newest first.' : 'The wires and corrections booked, newest first.'}
          </span>
        </div>
        <div className="qc-segmented" role="group" aria-label="Movements shown" style={{ marginBottom: 12 }}>
          <button
            type="button"
            aria-pressed={!everyMovement}
            onClick={() => {
              setEveryMovement(false);
              setEntriesPage(1);
            }}
          >
            Wires and corrections
          </button>
          <button
            type="button"
            aria-pressed={everyMovement}
            onClick={() => {
              setEveryMovement(true);
              setEntriesPage(1);
            }}
          >
            Every movement
          </button>
        </div>
        {!entries.data || entries.data.items.length === 0 ? (
          <p className="qc-empty">Nothing booked yet.</p>
        ) : (
          <>
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th>Amount</th>
                  <th>Ledger after</th>
                  <th>Reference / note</th>
                </tr>
              </thead>
              <tbody>
                {entries.data.items.map((e) => (
                  <tr key={e.id}>
                    <td>{shortDate(e.createdAt)}</td>
                    <td>{entryLabel(e.kind)}</td>
                    <td>{e.amount}</td>
                    <td>{e.balanceAfter}</td>
                    <td>{[e.reference, e.memo].filter(Boolean).join(' · ') || (e.loadId ? 'Card load' : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager
              page={entriesPage}
              pageSize={entries.data.pageSize}
              total={entries.data.total}
              onPage={setEntriesPage}
            />
          </>
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Balance readings</h3>
          <span>
            {isCp ? 'The MFA' : name} read every hour (and when asked), or typed in, against QOR&apos;s ledger then.
          </span>
        </div>
        {!reads.data || reads.data.items.length === 0 ? (
          <p className="qc-empty">Never read yet.</p>
        ) : (
          <>
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>{isCp ? 'MFA' : 'Balance'}</th>
                  <th>QOR ledger</th>
                  <th>Difference</th>
                  <th>In flight</th>
                  <th>How</th>
                </tr>
              </thead>
              <tbody>
                {reads.data.items.map((r) => (
                  <tr key={r.id}>
                    <td>{shortDate(r.takenAt)}</td>
                    <td>{r.balance}</td>
                    <td>{r.ledger}</td>
                    <td>
                      {r.difference}{' '}
                      {cents(r.difference) !== 0 && cents(r.difference) !== -cents(r.inFlight) && (
                        <span className="qc-badge qc-badge-warning">Check</span>
                      )}
                    </td>
                    <td>{r.inFlight}</td>
                    <td>{r.source === 'api' ? 'Read from the processor' : `Typed in by ${r.recordedBy ?? 'staff'}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager page={readsPage} pageSize={reads.data.pageSize} total={reads.data.total} onPage={setReadsPage} />
          </>
        )}
      </div>
    </>
  );
}
